import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ClipboardEdit, UserCheck, Users } from 'lucide-react';
import { FC, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useIsMobile } from '@/hooks/use-mobile';

interface Props {
  isOpen: boolean;
  setIsOpen: () => void;
  onSubmit: (payload: {
    officialValuation?: string;
    fairMarketValue?: string;
  }) => void;
}

const ValuationModal: FC<Props> = ({ isOpen, setIsOpen, onSubmit }) => {
  const [valuationType, setValuationType] = useState('');
  const [officialValuation, setOfficialValuation] = useState<string>('');
  const [fairMarketValue, setFairMarketValue] = useState<string>('');
  const isMobile = useIsMobile();

  const handleSubmit = () => {
    onSubmit({
      fairMarketValue,
      officialValuation,
    });

    setIsOpen();
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-[calc(100vw-32px)] md:max-w-lg">
        {!valuationType ? (
          <div className="w-full">
            <DialogHeader>
              <DialogTitle className="text-center text-2xl font-bold text-tax-deepBlue mb-2">
                Do you have an official notice from the assessor with your
                value?
              </DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-4 mt-6">
              <Button
                onClick={() => setValuationType('OFFICIAL')}
                className="bg-primary hover:bg-primary/90 text-primary-foreground text-lg font-medium flex items-center justify-center gap-3"
              >
                <UserCheck className="w-5 h-5 shrink-0" />
                {isMobile
                  ? 'Yes'
                  : 'Yes, I have an official notice from the assessor.'}
              </Button>

              <Button
                onClick={() => setValuationType('NON-OFFICIAL')}
                variant="outline"
                className="border-tax-borderGray hover:bg-tax-lightGray text-tax-neutralGray hover:text-tax-deepBlue text-lg font-medium flex items-center justify-center gap-3"
              >
                <Users className="w-5 h-5 shrink-0" />
                {isMobile
                  ? 'No'
                  : "No, I don't have an official notice from assessor"}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-center text-2xl font-bold text-tax-deepBlue mb-2">
                {valuationType === 'OFFICIAL'
                  ? `What is the assessor's official valuation?`
                  : `If you have an opinion of your property's fair market value enter that now. I not leave it blank and we'll estimate it.`}
              </DialogTitle>
            </DialogHeader>
            {valuationType === 'OFFICIAL' ? (
              <div className="mt-4 bg-tax-lightGray rounded-lg p-4 border-l-4 border-tax-brightBlue">
                <div className="flex items-center mb-2">
                  <ClipboardEdit className="h-5 w-5 mr-2 text-tax-brightBlue" />
                  <h4 className="text-base font-bold text-tax-brightBlue">
                    The official assessors valuation of our property:
                  </h4>
                </div>
                <div className="flex items-center">
                  <div className="relative flex-grow">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      $
                    </span>
                    <Input
                      type="text"
                      placeholder="0"
                      value={officialValuation}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^0-9]/g, ''); // keep only numbers
                        const formatted = raw.replace(
                          /\B(?=(\d{3})+(?!\d))/g,
                          ',',
                        );
                        setOfficialValuation(formatted);
                      }}
                      className="pl-8 bg-white"
                    />
                  </div>
                  {/* {officialValuation && (
                    <div className="ml-3 text-tax-deepBlue font-semibold">
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        maximumFractionDigits: 0,
                      }).format(Number(officialValuation))}
                    </div>
                  )} */}
                </div>
              </div>
            ) : (
              <div className="mt-4 bg-tax-lightGray rounded-lg p-4 border-l-4 border-tax-brightBlue">
                <div className="flex items-center mb-2">
                  <ClipboardEdit className="h-5 w-5 mr-2 text-tax-brightBlue" />
                  <h4 className="text-base font-bold text-tax-brightBlue">
                    Your opinion of the fair market value of your property as of
                    the assessment date:
                  </h4>
                </div>
                <div className="flex items-center">
                  <div className="relative flex-grow">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      $
                    </span>
                    <Input
                      type="text"
                      placeholder="0"
                      value={fairMarketValue}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^0-9]/g, ''); // keep only numbers
                        const formatted = raw.replace(
                          /\B(?=(\d{3})+(?!\d))/g,
                          ',',
                        );
                        setFairMarketValue(formatted);
                      }}
                      className="pl-8 bg-white"
                    />
                  </div>
                  {/* {fairMarketValue && (
                    <div className="ml-3 text-tax-deepBlue font-semibold">
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        maximumFractionDigits: 0,
                      }).format(Number(fairMarketValue))}
                    </div>
                  )} */}
                </div>
              </div>
            )}
            <div className="flex flex-col gap-4 mt-6">
              <Button
                onClick={handleSubmit}
                className="bg-primary hover:bg-primary/90 text-primary-foreground text-lg font-medium flex items-center justify-center gap-3"
              >
                {/* <UserCheck className="w-5 h-5" /> */}
                Confirm
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ValuationModal;
